/**
 * Alert Component - Controller
 * ==============================
 */

import type { ISCEService, ITranscludeFunction } from 'angular';
import {
  THEME_CLASSES,
  BASE_CLASSES,
  ICONS,
  DISMISS_ICON,
  DEFAULTS,
  AlertTheme,
  AlertIconType
} from './constants';

export interface AlertBindings {
  theme?: AlertTheme;
  title?: string;
  description?: string;
  showTitle?: boolean;
  showIcon?: boolean;
  icon?: AlertIconType;
  showDismiss?: boolean;
  onDismiss?: (args: { $event: Event }) => void;
}

interface BindingChanges {
  icon?: { currentValue: AlertIconType };
  [key: string]: unknown;
}

export class AlertController implements AlertBindings {
  static $inject = ['$sce', '$transclude'];

  private $sce: ISCEService | null;
  private $transclude: ITranscludeFunction | null;

  // Bindings
  theme?: AlertTheme;
  title?: string;
  description?: string;
  showTitle?: boolean;
  showIcon?: boolean;
  icon?: AlertIconType;
  showDismiss?: boolean;
  onDismiss?: (args: { $event: Event }) => void;

  // Estado interno (SVG confiables para ng-bind-html)
  trustedIcon: unknown = null;
  trustedDismissIcon: unknown = null;

  // Slots nombrados (isSlotFilled SÍ funciona para multi-slot, a diferencia
  // del slot default booleano) — el markup transcluido reemplaza title/
  // description/las acciones por defecto, parte por parte.
  hasTitleSlot = false;
  hasTextSlot = false;
  hasActionsSlot = false;

  constructor($sce: ISCEService, $transclude: ITranscludeFunction) {
    this.$sce = $sce;
    this.$transclude = $transclude;
    this.handleDismiss = this.handleDismiss.bind(this);
  }

  $onInit(): void {
    this.theme = this.theme || DEFAULTS.theme;
    this.icon = this.icon || DEFAULTS.icon;
    if (this.showTitle === undefined || this.showTitle === null) {
      this.showTitle = true;
    }
    if (this.showIcon === undefined || this.showIcon === null) {
      this.showIcon = true;
    }
    if (this.showDismiss === undefined || this.showDismiss === null) {
      this.showDismiss = true;
    }
    if (this.$transclude) {
      this.hasTitleSlot = this.$transclude.isSlotFilled('titleSlot');
      this.hasTextSlot = this.$transclude.isSlotFilled('textSlot');
      this.hasActionsSlot = this.$transclude.isSlotFilled('actionsSlot');
    }
    this._updateTrustedIcon();
    if (this.$sce) {
      this.trustedDismissIcon = this.$sce.trustAsHtml(DISMISS_ICON);
    }
  }

  $onChanges(changes: BindingChanges): void {
    if (changes.icon) {
      this._updateTrustedIcon();
    }
  }

  $onDestroy(): void {
    this.$sce = null;
    this.$transclude = null;
  }

  private _updateTrustedIcon(): void {
    if (!this.$sce) return;
    this.trustedIcon = this.$sce.trustAsHtml(ICONS[this.icon || DEFAULTS.icon] || '');
  }

  handleDismiss($event: Event): void {
    $event.stopPropagation();
    if (typeof this.onDismiss === 'function') {
      this.onDismiss({ $event });
    }
  }

  private get themeClasses() {
    return THEME_CLASSES[this.theme || DEFAULTS.theme];
  }

  // ---- Clases ----

  getContainerClasses(): string[] {
    return [BASE_CLASSES.container, this.themeClasses.container];
  }

  getRowClasses(): string[] {
    return [BASE_CLASSES.row];
  }

  getIconClasses(): string[] {
    return [BASE_CLASSES.icon, this.themeClasses.icon];
  }

  getContentClasses(): string[] {
    return [BASE_CLASSES.content];
  }

  getTextBlockClasses(): string[] {
    return [BASE_CLASSES.textBlock];
  }

  getTitleClasses(): string[] {
    return [BASE_CLASSES.title, this.themeClasses.title];
  }

  getTextClasses(): string[] {
    return [BASE_CLASSES.text, this.themeClasses.text];
  }

  getActionsClasses(): string[] {
    return [BASE_CLASSES.actions];
  }

  getDismissClasses(): string[] {
    return [BASE_CLASSES.dismiss, this.themeClasses.dismiss];
  }
}

export default AlertController;
